import { useEffect, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api, ApiError, type BoxState } from "../api";
import { peekPendingClaim, rememberClaim } from "../claim";
import { AuthError, AuthField, AuthShell } from "../components/AuthShell";

const STEPS = [
  "Plug the box into power and connect its network cable to your router.",
  "Wait for the light on the box to stay on. This usually takes a few minutes.",
  "Enter the serial number and claim code printed on the label.",
];

const STATES: { key: BoxState; label: string; hint: string }[] = [
  { key: "waiting", label: "Waiting for the box", hint: "Check the power and the network cable." },
  { key: "reconnecting", label: "Box found, reconnecting", hint: "It reported in before and is coming back." },
  { key: "online", label: "Box is online", hint: "It is ready to be added to your account." },
];

/** Where a box is ready to be set up: its label proves ownership, and the
 *  page shows when the box has reached IntraScene. */
export default function RegisterBox() {
  const nav = useNavigate();
  const pending = peekPendingClaim();
  const [serial, setSerial] = useState(pending?.serial ?? "");
  const [code, setCode] = useState(pending?.claim_code ?? "");
  const [submitted, setSubmitted] = useState<{ serial: string; code: string } | null>(
    pending ? { serial: pending.serial, code: pending.claim_code } : null,
  );
  const [signedIn, setSignedIn] = useState(false);

  useEffect(() => {
    api.me().then(
      () => setSignedIn(true),
      () => setSignedIn(false),
    );
  }, []);

  const status = useQuery({
    queryKey: ["box-status", submitted?.serial, submitted?.code],
    queryFn: () => api.boxStatus(submitted!.serial, submitted!.code),
    enabled: submitted !== null,
    retry: false,
    refetchInterval: (q) =>
      q.state.error || q.state.data?.state === "online" ? false : 4_000,
  });

  const state = status.data?.state;
  const failure = status.error
    ? status.error instanceof ApiError && status.error.status === 400
      ? "That serial number and claim code do not match a box that is ready to set up."
      : status.error instanceof ApiError && status.error.status === 429
        ? "Too many attempts. Wait a minute and try again."
        : "Could not reach IntraScene. Check your connection and try again."
    : null;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const next = { serial: serial.trim().toUpperCase(), code: code.trim().toUpperCase() };
    if (submitted && submitted.serial === next.serial && submitted.code === next.code) {
      void status.refetch();
    } else {
      setSubmitted(next);
    }
  }

  function addToAccount() {
    if (!submitted) return;
    rememberClaim(`${submitted.serial}.${submitted.code}`);
    nav(signedIn ? { to: "/settings", search: { section: "connectors" } } : { to: "/signup" });
  }

  return (
    <AuthShell
      wide
      title="Set up your box"
      subtitle="Connect the box, then add it to your account."
      footer={
        <p>
          <Link to="/login" className="font-medium text-brand-fuchsia hover:underline">
            Back to sign in
          </Link>
        </p>
      }
    >
      <ol className="mb-6 space-y-2 text-sm text-bf-muted">
        {STEPS.map((s, i) => (
          <li key={s} className="flex gap-3">
            <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-white/10 text-xs text-white">
              {i + 1}
            </span>
            <span>{s}</span>
          </li>
        ))}
      </ol>

      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <AuthField
            label="Serial number"
            value={serial}
            onChange={(e) => setSerial(e.target.value)}
            placeholder="ISC-2610-0001"
            autoComplete="off"
            required
          />
          <AuthField
            label="Claim code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="ABCD-EFGH"
            autoComplete="off"
            required
          />
        </div>
        <AuthError message={failure} />
        <button className="bf-button w-full py-2.5" type="submit" disabled={status.isFetching}>
          {failure ? "Try again" : "Check the box"}
        </button>
      </form>

      {state && (
        <div className="mt-6 space-y-3" aria-live="polite">
          <ul className="space-y-2 text-sm">
            {STATES.map((s) => {
              const at = STATES.findIndex((x) => x.key === state);
              const idx = STATES.findIndex((x) => x.key === s.key);
              const here = idx === at;
              return (
                <li
                  key={s.key}
                  className={here ? "text-white" : idx < at ? "text-bf-muted" : "text-bf-muted/40"}
                >
                  <span className="font-medium">{s.label}</span>
                  {here && <span className="block text-xs text-bf-muted">{s.hint}</span>}
                </li>
              );
            })}
          </ul>
          <button
            className="bf-button w-full py-2.5 disabled:opacity-40"
            disabled={state !== "online"}
            onClick={addToAccount}
          >
            {signedIn ? "Add to my account" : "Create my account"}
          </button>
        </div>
      )}
    </AuthShell>
  );
}
